import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { GlobalKeyListenerService } from '@observation/services/global-key-listener.service';
import { UserSettingsStateService } from '@services/user-settings.state.service';
import { API_URL, APP_CONFIG, ConfigSettings, ROUTES } from '@shared/config';
import {
  FONT_FROM_COMPANY,
  FONT_FROM_COMPANY_DEFAULT,
  FontFamily,
} from '@shared/config/fonts.config';
import { defaultTheme } from '@shared/config/themes';
import { BackgroundAnimationImageType } from '@shared/models/background-animation.model';
import { BackgroundAnimationService } from '@shared/services/background-animation.service';
import {
  BackgroundAsset,
  NONE_BACKGROUND_ASSET,
} from '@shared/components/background/background.model';
import { ORNavigationService, OrUserSettingsService } from '@shared/services';
import { VisibilityAreaService } from '@shared/services/visibility-area.service';
import { UsageTrackingFactory } from '@shared/usage-tracking/factories/usage-tracking.factory';
import {
  ACTION_TYPES,
  TRACKING_INCLUDED_URLS,
} from '@shared/usage-tracking/usage-tracking.config';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { MsalAuthenticationService } from '@security/msal-authentication.service';
import { FileUrlService } from '@shared/services/file-url.service';
import { BrandingFileRole, FileEntity } from '@shared/models/file.model';

@Component({
  selector: 'od-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private theme$ = this.userSettingsStateService.theme$;
  loading$: Observable<boolean> = this.orNavigationService.navigationLoading$;
  themeLoading$: Observable<boolean> = this.orUserSettingsService.themeLoading$;

  private readonly iconUrl: string;

  private backgroundType: BehaviorSubject<BackgroundAnimationImageType> =
    new BehaviorSubject<BackgroundAnimationImageType>(BrandingFileRole.LANDING);
  public backgroundAsset$: Observable<BackgroundAsset>;
  public readonly noneBackgroundAsset = NONE_BACKGROUND_ASSET;

  private layoutId$ = new BehaviorSubject<number | null>(
    this.extractLayoutId(window.location.pathname)
  );
  private landingId$ = new BehaviorSubject<number | null>(
    this.extractLandingId(window.location.pathname)
  );

  private subscriptions = new Subject<void>();
  private favIcon: HTMLLinkElement = document.querySelector('#favIcon');
  public appFont: FontFamily;
  public visibilityAreaClasses$: Observable<string>;

  constructor(
    private router: Router,
    private orNavigationService: ORNavigationService,
    private orUserSettingsService: OrUserSettingsService,
    private userSettingsStateService: UserSettingsStateService,
    private globalKeyListenerService: GlobalKeyListenerService,
    private visibilityAreaService: VisibilityAreaService,
    private fileUrlService: FileUrlService,
    private backgroundAnimationService: BackgroundAnimationService,
    private readonly msalAuthService: MsalAuthenticationService,
    @Inject(APP_CONFIG) { company }: ConfigSettings
  ) {
    this.iconUrl = this.fileUrlService.getFileUrl({
      entity: FileEntity.BRANDING,
      role: BrandingFileRole.ICON,
      branding: defaultTheme,
      content: true,
    });
    this.appFont = FONT_FROM_COMPANY[company] ?? FONT_FROM_COMPANY_DEFAULT;

    this.backgroundAsset$ = combineLatest([
      this.theme$,
      this.backgroundType,
      this.layoutId$,
      this.landingId$,
    ]).pipe(
      distinctUntilChanged(
        (
          [
            previousTheme,
            previousBackgroundType,
            previousLayoutId,
            previousLandingId,
          ],
          [
            currentTheme,
            currentBackgroundType,
            currentLayoutId,
            currentLandingId,
          ]
        ) =>
          previousTheme === currentTheme &&
          previousBackgroundType === currentBackgroundType &&
          previousLayoutId === currentLayoutId &&
          previousLandingId === currentLandingId
      ),
      switchMap(([theme, backgroundType, layoutId, landingId]) => {
        if (landingId != null) {
          return this.backgroundAnimationService.resolveLandingBackground(
            theme,
            landingId
          );
        }
        if (layoutId != null) {
          return this.backgroundAnimationService.resolveLayoutBackground(
            theme,
            layoutId
          );
        }
        return this.backgroundAnimationService.resolveAsset(
          theme,
          backgroundType
        );
      }),
      shareReplay(1)
    );
  }

  private resizeTimeout: ReturnType<typeof setTimeout>;

  @HostListener('window:resize')
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.setViewportHeight(), 150);
  }

  @HostListener('window:keyup.arrowLeft')
  handleArrowLeft() {
    this.globalKeyListenerService.setArrowLeftPressed();
    return false;
  }

  @HostListener('window:keyup.arrowRight')
  handleArrowRight() {
    this.globalKeyListenerService.setArrowRightPressed();
    return false;
  }

  ngOnInit() {
    const validURLs: string[] = [
      ROUTES.login,
      API_URL.shouldLogin,
      API_URL.landing,
    ];
    this.favIcon.href = this.iconUrl;
    this.router.events.pipe(takeUntil(this.subscriptions)).subscribe(event => {
      if (event instanceof RouteConfigLoadStart) {
        this.orNavigationService.setNavigationLoading(true);
      } else if (
        event instanceof NavigationError ||
        event instanceof NavigationCancel
      ) {
        this.orNavigationService.setNavigationLoading(false);
      } else if (event instanceof NavigationEnd) {
        this.orNavigationService.setNavigationLoading(false);
        const nextBackgroundType = validURLs.some(url =>
          event.url.includes(url)
        )
          ? BrandingFileRole.LANDING
          : BrandingFileRole.BACKGROUND;

        if (this.backgroundType.getValue() !== nextBackgroundType) {
          this.backgroundType.next(nextBackgroundType);
        }

        const nextLayoutId = this.extractLayoutId(event.url);
        if (this.layoutId$.getValue() !== nextLayoutId) {
          this.layoutId$.next(nextLayoutId);
        }
        const nextLandingId = this.extractLandingId(event.url);
        if (this.landingId$.getValue() !== nextLandingId) {
          this.landingId$.next(nextLandingId);
        }
        if (event.url.includes(TRACKING_INCLUDED_URLS.dashboard)) {
          UsageTrackingFactory.createTrackingService(
            ACTION_TYPES.dashboardNavigation
          ).addUsageTracking();
        } else if (event.url.includes(TRACKING_INCLUDED_URLS.landing)) {
          UsageTrackingFactory.createTrackingService(
            ACTION_TYPES.landingNavigation
          ).addUsageTracking();
        }
      }
    });
    this.visibilityAreaClasses$ =
      this.visibilityAreaService.getVisibilityAreaClasses();

    // Re-hydrated MSAL session on application startup
    if (this.msalAuthService.isMsalAuthenticationEnabled()) {
      this.msalAuthService.restoreSession();
    }

    this.setViewportHeight();
  }

  private setViewportHeight(): void {
    if (CSS.supports('height', '1dvh')) return;
    document.documentElement.style.setProperty(
      '--vh',
      `${window.innerHeight * 0.01}px`
    );
  }

  private extractLayoutId(url: string): number | null {
    const match = url.match(/\/dashboard\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  private extractLandingId(url: string): number | null {
    const match = url.match(/\/landing\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  ngOnDestroy() {
    this.subscriptions.next();
    this.subscriptions.unsubscribe();
  }
}
